import { faChevronDown, faRotateExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { SimpleProduct } from "@tosize/shapes/src/types/product";
import {
	Button,
	FormErrorMessage,
	ProductGroup,
	ProductionUnit,
	Skeleton,
	Tooltip,
	Typography,
	cn,
	formatNumber,
	formatUnit,
} from "@tosize/ui";
import { RadioGroup } from "radix-ui";
import { Fragment, useCallback, useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useCardContext } from "../../../context/CardContext";
import { useConfig } from "../../../context/ConfigContext";
import { useErrorContext } from "../../../context/ErrorContext";
import { usePostDataContext } from "../../../context/PostDataContext";
import useProducts from "../../../hooks/useProducts";
import { RadioGroupItem } from "../../RadioGroupItem";
import { Step } from "../../Steps/Step";
import { TYPE_STEP_IDENTIFIER } from "./ProductionType";
import { SHAPE_DIMENSIONS_STEP_IDENTIFIER } from "./ShapeDimensions";

export const THICKNESS_STEP_IDENTIFIER = "thickness";

export function Thickness() {
	const intl = useIntl();
	const { initialWorkpiece, productGroup } = useConfig();
	const { products, product, productData } = useProducts();
	const { data, setData, defaultData, defaultWorkpiece, setWorkpiece } = usePostDataContext();
	const { errors, setErrors } = useErrorContext();
	const { navigateToCard } = useCardContext();

	const setCurrentThickness = useCallback(
		(thickness: string, scroll?: boolean) => {
			const currentProduct = products?.find((item) => item.id === thickness);

			const initialShapeDimensions = currentProduct?.checkModeEnabled
				? { width: 1000, length: 1000 }
				: undefined;

			setErrors({
				product: [],
				shape: [],
				dimensions: {},
				accepted_condition: [],
			});
			setData({
				...defaultData,
				product: thickness,
				sku: currentProduct?.sku,
			});
			setWorkpiece({
				...defaultWorkpiece,
				...initialWorkpiece,
				shape_dimensions: {
					...defaultWorkpiece.shape_dimensions,
					...initialShapeDimensions,
					...initialWorkpiece?.shape_dimensions,
				},
			});
			navigateToCard(
				THICKNESS_STEP_IDENTIFIER,
				productGroup === ProductGroup.Plastic_Sheet
					? SHAPE_DIMENSIONS_STEP_IDENTIFIER
					: TYPE_STEP_IDENTIFIER,
				!!scroll,
			);
		},
		[
			defaultData,
			defaultWorkpiece,
			initialWorkpiece,
			productGroup,
			products,
			navigateToCard,
			setErrors,
			setData,
			setWorkpiece,
		],
	);

	const formatValue = useCallback(
		(product: SimpleProduct) => {
			return formatNumber({
				lang: intl.locale,
				value: product.thickness,
				unit: "millimeter",
				digits: { min: 0, max: 0 },
			});
		},
		[intl.locale],
	);

	const selectedValue = useMemo(() => {
		const selectedProduct = products?.find((item) => item.id === product);

		return selectedProduct ? formatValue(selectedProduct) : undefined;
	}, [formatValue, product, products]);

	const filteredProducts = useMemo(() => products?.filter((item) => !!item.stock), [products]);

	const currentValue = useMemo(
		() => filteredProducts?.find((item) => item.id === data.product),
		[data.product, filteredProducts],
	);

	// set thickness if there is only one option
	// and no option is selected in the initial postData
	useEffect(() => {
		if (!data.product && filteredProducts?.length === 1) {
			setCurrentThickness(String(filteredProducts[0]?.id));
		}
	}, [data.product, filteredProducts, setCurrentThickness]);

	const title = useMemo(() => {
		if (!productData) {
			return intl.formatMessage({ defaultMessage: "Dikte" });
		}

		if (productGroup === ProductGroup.Wood_Beams) {
			return intl.formatMessage({ defaultMessage: "Kopmaat" });
		}

		return intl.formatMessage({ defaultMessage: "Dikte" });
	}, [intl, productData, productGroup]);

	const productsByProductionUnit = useMemo(() => {
		if (products) {
			const ProductByUnit = products
				.sort((a, b) => a.thickness - b.thickness)
				.reduce(
					(acc, product) => {
						const productionUnit = product.available_production_units?.length
							? product.available_production_units.sort().join(",")
							: ProductionUnit.M2;

						if (!acc[productionUnit]) {
							acc[productionUnit] = [];
						}

						acc[productionUnit]?.push(product);

						return acc;
					},
					{} as Record<string, SimpleProduct[]>,
				);

			return Object.entries(ProductByUnit)
				.map(([key, products]) => ({ key, products }))
				.sort((a, b) =>
					a.key === ProductionUnit.CompletePanel || b.key === ProductionUnit.M2
						? 1
						: a.key === ProductionUnit.M2 || b.key === ProductionUnit.CompletePanel
							? -1
							: 0,
				);
		}
	}, [products]);

	return (
		<Step
			title={title}
			value={THICKNESS_STEP_IDENTIFIER}
			selected={!!selectedValue}
			selectedValue={selectedValue}
		>
			<RadioGroup.Root
				value={currentValue?.id}
				onValueChange={(value) => setCurrentThickness(value, true)}
				className="space-y-4"
			>
				{productsByProductionUnit === undefined
					? Array.from({ length: 2 }).map((_value, index) => (
							<div key={index}>
								<Skeleton className="mb-2 h-4" />
								<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4 md:grid-cols-4">
									{Array.from({ length: 4 }).map((_value, index) => (
										<Skeleton key={index} className="h-[42px]" />
									))}
								</div>
							</div>
						))
					: productsByProductionUnit.map(({ key, products }, index) => (
							<div key={index}>
								<Typography color="text-shades-dark" className="mb-2 text-sm">
									<FormattedMessage
										defaultMessage="Koop per {unit}"
										values={{
											unit: intl.formatList(
												key.split(",").map((item) => formatUnit(intl, item)),
												{ type: "conjunction" },
											),
										}}
										id="ylvQW1"
									/>
								</Typography>

								<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4 md:grid-cols-4">
									{products.map((product) => (
										<Fragment key={product.id}>
											{product.stock ? (
												<RadioGroupItem
													value={product.id}
													onClick={() =>
														currentValue?.id === product.id && setCurrentThickness(product.id, true)
													}
												>
													{formatValue(product)}
												</RadioGroupItem>
											) : (
												<ThicknessNotAvailable product={product} />
											)}
										</Fragment>
									))}
								</div>
							</div>
						))}
			</RadioGroup.Root>

			{errors.product.map((issue) => (
				<FormErrorMessage key={issue.code} variant="danger">
					{issue.message}
				</FormErrorMessage>
			))}

			<div className="mt-4 flex justify-end">
				{productGroup === ProductGroup.Plastic_Sheet ? (
					<Button
						color="success"
						endicon={faChevronDown}
						onClick={() =>
							navigateToCard(THICKNESS_STEP_IDENTIFIER, SHAPE_DIMENSIONS_STEP_IDENTIFIER, true)
						}
						disabled={!product}
					>
						<FormattedMessage defaultMessage="Afmetingen opgeven" />
					</Button>
				) : (
					<Tooltip content={<FormattedMessage defaultMessage="Kies een dikte om door te gaan." />}>
						<Button
							color="success"
							endicon={faChevronDown}
							onClick={() => navigateToCard(THICKNESS_STEP_IDENTIFIER, TYPE_STEP_IDENTIFIER, true)}
							disabled={!product}
						>
							<FormattedMessage defaultMessage="Afnametype kiezen" />
						</Button>
					</Tooltip>
				)}
			</div>
		</Step>
	);
}

function ThicknessNotAvailable({ product }: { product: SimpleProduct }) {
	const intl = useIntl();

	return (
		<Tooltip
			content={
				product.stock_message ? (
					<span dangerouslySetInnerHTML={{ __html: product.stock_message }} />
				) : (
					intl.formatMessage({
						defaultMessage:
							"Deze dikte is tijdelijk niet beschikbaar, kies een andere dikte om door te gaan",
					})
				)
			}
		>
			<button
				type="button"
				value={product.id}
				title={formatNumber({
					lang: intl.locale,
					value: product.thickness,
					unit: "millimeter",
					digits: { min: 0, max: 0 },
				})}
				className={cn(
					"flex h-full w-full cursor-not-allowed items-center justify-center gap-2 border border-shades-light bg-shades-light py-2 text-center opacity-70 ring-0 ring-transparent transition hover:border-primary focus:outline-none focus-visible:border-primary motion-reduce:transition-none",
					{ "text-secondary": !!product.stock_message },
				)}
			>
				{!!product.stock_message && <FontAwesomeIcon icon={faRotateExclamation} fixedWidth />}

				{formatNumber({
					lang: intl.locale,
					value: product.thickness,
					unit: "millimeter",
					digits: { min: 0, max: 0 },
				})}
			</button>
		</Tooltip>
	);
}
